// ============================================================
// sectionKit.tsx
// Shared primitives for the redesigned CASE SECTIONS (Stage 2 — approved
// mockup casevo-case-sections-batch1.html, "One pattern for every case
// section"). Same visual language as ClientInfoSections (its CARD / BTN /
// INP constants are imported, not copied):
//   • CaseSectionFrameContext — ViewingPane hands each redesigned section
//     its group eyebrow + status chip (mockup .shead .e)
//   • SectionHead      — eyebrow + 20px title + sub + actions (.shead)
//   • Glance           — the 4-cell at-a-glance strip (.glance)
//   • SectionFooter    — replaces each section's hand-rolled "Mark as
//                        complete and continue" block (.sfoot)
//   • view helpers     — Kv / Empty / chips / plate / cond tag (.kdl …)
//   • edit helpers     — Yes/No segmented radios, revealed block, tiles
//   • live documents   — LiveDocEdit (editor frame + "Live" badge) and
//                        LiveDocView (read-only live doc, clamped)
// Nothing here saves anything: every write goes through the callbacks the
// section passes in (its existing update / updateCase / broadcast paths).
// ============================================================
import React, { useContext, useLayoutEffect, useRef, useState } from 'react'
import DOMPurify from 'dompurify'
import RichTextEditor from '../../shared/RichTextEditor'
import type { CollabUser } from '../../shared/RichTextEditor'
import { useCasevoStore } from '../../../store/useCasevoStore'
import { useSectionCompletion, type CompletableSection } from '../../../store/useSectionCompletion'
import { getVisibleSequentialKeys, getNextSequentialKey, getSectionLabel, advanceCaseSection } from '../../../utils/sectionFlow'
import type { ValidationError } from '../../../utils/validation'
import { CARD, BTN_SEC, BTN_PRI } from '../../clientInfo/ClientInfoSections'
import { CaseStatusMark } from '../caseUi'
import { flushLiveDocSnapshots } from '../../shared/liveDocFlush'
import { CASE_NAV_INDEX } from '../../../constants/caseNav.constants'

// ── frame context (ViewingPane → section) ────────────────────
export interface CaseSectionFrame {
  group: string
  chip: React.ReactNode
}
export const CaseSectionFrameContext = React.createContext<CaseSectionFrame | null>(null)
export const useCaseSectionFrame = (): CaseSectionFrame | null => useContext(CaseSectionFrameContext)

// ── icons (Feather, currentColor) ────────────────────────────
const P = {
  cal: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  map: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>,
  pulse: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
  car: <><path d="M5 17h14M5 17a2 2 0 1 0 4 0M15 17a2 2 0 1 0 4 0" /><path d="M3 17V11l2-5h14l2 5v6" /></>,
  heart: <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />,
  brain: <><path d="M9.5 2A2.5 2.5 0 0 0 7 4.5v.5a3 3 0 0 0-3 3v1a3 3 0 0 0 0 6v1a3 3 0 0 0 3 3h.5A2.5 2.5 0 0 0 10 21.5V2.5" /><path d="M14.5 2A2.5 2.5 0 0 1 17 4.5v.5a3 3 0 0 1 3 3v1a3 3 0 0 1 0 6v1a3 3 0 0 1-3 3h-.5a2.5 2.5 0 0 1-2.5 2.5V2.5" /></>,
  alert: <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
  text: <><line x1="17" y1="10" x2="3" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="17" y1="18" x2="3" y2="18" /></>,
  sync: <><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></>,
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  quote: <><path d="M3 21c3 0 7-1 7-8V5H3v8h4" /><path d="M14 21c3 0 7-1 7-8V5h-7v8h4" /></>,
  phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />,
  mail: <><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" /><path d="M22 6l-10 7L2 6" /></>,
  time: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
  plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
  dots: <><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></>,
  trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>,
  pen: <path d="M4 20h4L19 9l-4-4L4 16v4z" />,
  arrow: <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>,
  right: <polyline points="9 18 15 12 9 6" />,
  check: <path d="M20 6 9 17l-5-5" />,
  agency: <><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></>,
  steth: <><path d="M4 3v6a6 6 0 0 0 12 0V3" /><circle cx="18" cy="16" r="3" /><path d="M10 15v2a4 4 0 0 0 8 0" /></>,
  scale: <><path d="M12 3v18" /><path d="M5 7h14" /><path d="M5 7l-3 7a4 4 0 0 0 6 0z" /><path d="M19 7l-3 7a4 4 0 0 0 6 0z" /></>,
  flag: <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></>,
  bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>,
  chev: <polyline points="6 9 12 15 18 9" />,
  chevUp: <polyline points="18 15 12 9 6 15" />,
  file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>,
  shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>,
  umbrella: <><path d="M23 12a11.05 11.05 0 0 0-22 0zm-5 7a3 3 0 0 1-6 0v-7" /></>,
  pound: <><path d="M18 7a4 4 0 0 0-7.5-2c-.6 1-.5 2.6-.5 4v11" /><path d="M6 20h12M6 13h9" /></>,
  link: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>,
  history: <><path d="M3 3v5h5" /><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" /><path d="M12 7v5l4 2" /></>,
  cog: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
  lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
  open: <><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6M10 14L21 3" /></>,
  save: <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></>,
  unlink: <><path d="M18.84 12.25l1.72-1.71a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M5.17 11.75l-1.71 1.71a5 5 0 0 0 7.07 7.07l1.71-1.71" /><line x1="8" y1="2" x2="8" y2="5" /><line x1="2" y1="8" x2="5" y2="8" /><line x1="16" y1="19" x2="16" y2="22" /><line x1="19" y1="16" x2="22" y2="16" /></>,
  upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>,
  layers: <><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>,
  hash: <><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></>,
} as const
export type KitIconName = keyof typeof P
export const KIcon: React.FC<{ name: KitIconName; size?: number; strokeWidth?: number; className?: string }> = ({ name, size = 16, strokeWidth = 2, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`shrink-0 ${className}`}>{P[name]}</svg>
)

// ── section head (mockup .shead, :223-227) ───────────────────
export const SectionHead: React.FC<{ title: React.ReactNode; sub?: React.ReactNode; actions?: React.ReactNode }> = ({ title, sub, actions }) => {
  const frame = useCaseSectionFrame()
  return (
    <div className="flex items-end gap-3 mb-[14px] flex-wrap">
      <div className="flex-1 min-w-0">
        {frame && (
          <div className="flex items-center gap-[8px] text-[11px] tracking-[0.16em] uppercase font-bold text-casevo-text-soft">
            {frame.group}
            {frame.chip}
          </div>
        )}
        <h2 className={`${frame ? 'mt-[3px]' : 'mt-0'} mb-0 text-[20px] font-semibold tracking-[-0.01em] text-casevo-forest`}>{title}</h2>
        {sub && <div className="text-[13px] text-casevo-text-soft mt-[2px]">{sub}</div>}
      </div>
      {actions && <div className="flex gap-2 items-center shrink-0">{actions}</div>}
    </div>
  )
}

// ── at-a-glance strip (mockup .glance/.gc/.gl/.gv/.gs, :238-242) ──
export interface GlanceCell { icon: KitIconName; label: string; value: React.ReactNode; sub: React.ReactNode }
const GLANCE_BORDERS = ['border-r border-b md:border-b-0', 'border-b md:border-b-0 md:border-r', 'border-r', '']
// Two-cell strips (e.g. Funding) split evenly at every width.
const GLANCE_BORDERS_2 = ['border-r', '']
export const Glance: React.FC<{ cells: GlanceCell[] }> = ({ cells }) => (
  <div className={`${CARD} grid ${cells.length === 2 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'} overflow-hidden mb-[14px]`}>
    {cells.map((c, i) => (
      <div key={c.label} className={`py-[13px] px-4 min-w-0 border-casevo-border-soft ${(cells.length === 2 ? GLANCE_BORDERS_2 : GLANCE_BORDERS)[i] ?? ''}`}>
        <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-casevo-text-soft flex items-center gap-1.5"><KIcon name={c.icon} size={13} />{c.label}</div>
        <div className="text-[14.5px] font-semibold mt-[3px] truncate text-casevo-forest">{c.value}</div>
        <div className="text-[12px] text-casevo-text-soft truncate">{c.sub}</div>
      </div>
    ))}
  </div>
)
export const GlanceDash: React.FC = () => <span className="text-casevo-text-soft font-normal">—</span>

// ── section footer (mockup .sfoot, :353-356) ─────────────────
// Same completion flow every section used: validation gate → (optional
// pre-step, e.g. Witnesses' flushWitnessAutoSave) → markComplete(key, next)
// → advanceCaseSection(next). When already complete it offers "Go to next"
// (advanceCaseSection — the same flush-all + panel swap).
export const SectionFooter: React.FC<{
  sectionKey: CompletableSection
  errors: ValidationError[]
  beforeComplete?: () => Promise<void>
  // Sub-line when incomplete and nothing is missing (Witnesses: "No required fields…").
  readySub?: string
  // Title when incomplete and nothing is missing (default "Ready to mark complete").
  readyTitle?: string
}> = ({ sectionKey, errors, beforeComplete, readySub, readyTitle }) => {
  const completed = useSectionCompletion((s) => s.completedSections.has(sectionKey))
  const markComplete = useSectionCompletion((s) => s.markComplete)
  const viewingPaneToggles = useCasevoStore((s) => s.viewingPaneToggles)
  const userRole = useCasevoStore((s) => s.userRole)
  const next = getNextSequentialKey(sectionKey, getVisibleSequentialKeys(viewingPaneToggles, userRole))
  const nextLabel = next ? (CASE_NAV_INDEX[next]?.label ?? getSectionLabel(next)) : null
  const n = errors.length
  const handleMarkComplete = async (): Promise<void> => {
    if (errors.length > 0) return
    // An open live document's pending snapshot first, so the flushes below
    // (beforeComplete / advanceCaseSection) carry it instead of it landing
    // after the panel swaps.
    await flushLiveDocSnapshots()
    if (beforeComplete) await beforeComplete()
    // Re-read the visible chain at click time (same as the old handlers).
    const s = useCasevoStore.getState()
    const nxt = getNextSequentialKey(sectionKey, getVisibleSequentialKeys(s.viewingPaneToggles, s.userRole))
    markComplete(sectionKey, nxt)
    if (nxt) void advanceCaseSection(nxt)
  }
  let title: string
  let sub: string
  if (completed) {
    title = 'Complete'
    sub = n > 0
      ? `Marked complete, but ${n} required field${n === 1 ? ' is' : 's are'} now missing: ${errors.map((e) => e.message.replace(/ is required$| must be selected$/, '')).join(' · ')}.`
      : 'Marked complete. Edits still save; the tick stays.'
  } else if (n > 0) {
    title = `${n} required field${n === 1 ? '' : 's'} to finish`
    sub = errors.map((e) => e.message).join(' · ')
  } else {
    title = readyTitle ?? 'Ready to mark complete'
    sub = readySub ?? (nextLabel ? `Required fields are filled. Marking complete unlocks ${nextLabel}.` : 'Required fields are filled. This is the last setup step.')
  }
  return (
    <div className={`${CARD} flex items-center gap-3 flex-wrap mt-4 py-[13px] pr-4 pl-[18px]`}>
      <CaseStatusMark status={completed ? 'complete' : 'in_progress'} size={18} />
      <div className="text-[13.5px] text-casevo-forest min-w-0 flex-1">
        {title}
        <span className="block text-[12px] text-casevo-text-soft">{sub}</span>
      </div>
      <div className="ml-auto flex gap-2 items-center">
        {completed ? (
          next && nextLabel && (
            <button type="button" className={BTN_SEC} onClick={() => { void advanceCaseSection(next) }}>
              Go to {nextLabel}<KIcon name="arrow" size={15} />
            </button>
          )
        ) : (
          <button
            type="button"
            className={BTN_PRI}
            disabled={n > 0}
            title={n > 0 ? 'Complete all required fields to continue' : undefined}
            onClick={() => { void handleMarkComplete() }}
          >
            {next ? 'Mark complete & continue' : 'Mark complete'}<KIcon name="arrow" size={15} />
          </button>
        )}
      </div>
    </div>
  )
}

// ── view helpers (mockup .kdl/.kdt/.kdd/.big/.mono/.mt/.chp/.cond/.plate) ──
export const DL = 'grid grid-cols-1 sm:grid-cols-2 gap-x-[18px] gap-y-3 m-0'
export const DL1 = 'grid grid-cols-1 gap-y-3 m-0'
const KDT = 'text-[11px] font-bold tracking-[0.1em] uppercase text-casevo-text-soft mb-[3px] flex items-center gap-1.5'
const KDD = 'm-0 text-[14.5px] leading-[1.45] text-casevo-forest flex items-center gap-[7px] flex-wrap [overflow-wrap:anywhere]'
export const BIG = 'text-[17px] font-semibold tracking-[-0.005em]'
export const MONO = 'font-mono text-[13.5px] tracking-[0.04em]'
export const CHIP = 'inline-flex items-center gap-[5px] whitespace-nowrap text-[12px] font-semibold px-[9px] py-[2px] rounded-full bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5]'
export const CHIP_N = 'inline-flex items-center gap-[5px] whitespace-nowrap text-[12px] font-semibold px-[9px] py-[2px] rounded-full bg-casevo-border-soft text-casevo-text-soft'
export const PLATE = 'font-mono font-bold text-[14px] tracking-[0.08em] px-[9px] py-[2px] rounded-[5px] border-[1.5px] border-casevo-forest bg-casevo-surface'
export const LINK = 'text-casevo-forest no-underline border-b border-casevo-border-soft hover:text-casevo-brand hover:border-casevo-mid focus:outline-none casevo-focus rounded-[2px]'

export const CondTag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[10.5px] font-semibold text-casevo-text-soft border border-dashed border-casevo-border rounded-[5px] px-[6px] normal-case tracking-normal">{children}</span>
)
export const Kv: React.FC<{ label: string; cond?: string; full?: boolean; children: React.ReactNode }> = ({ label, cond, full, children }) => (
  <div className={`min-w-0 ${full ? 'col-span-full' : ''}`}>
    <dt className={KDT}>{label}{cond && <CondTag>{cond}</CondTag>}</dt>
    <dd className={KDD}>{children}</dd>
  </div>
)
export const Empty: React.FC<{ onAdd?: () => void }> = ({ onAdd }) => (
  <span className="text-casevo-text-soft italic text-[13.5px]">
    Not recorded
    {onAdd && (
      <button type="button" onClick={onAdd} className="not-italic ml-1.5 text-casevo-brand dark:text-casevo-mid font-semibold text-[12.5px] focus:outline-none casevo-focus rounded-[3px]">Add</button>
    )}
  </span>
)
export const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-casevo-text-soft text-[13.5px]">{children}</span>
)
export const YesNo: React.FC<{ value: boolean | null; onAdd?: () => void }> = ({ value, onAdd }) =>
  value === true ? <span className={CHIP}><KIcon name="check" size={12} strokeWidth={3} />Yes</span>
    : value === false ? <span className={CHIP_N}>No</span>
      : <Empty onAdd={onAdd} />
export const AddressBlock: React.FC<{ lines: string[]; postcode?: string }> = ({ lines, postcode }) => (
  <div className="min-w-0">
    {lines.map((l, i) => <React.Fragment key={i}>{i > 0 && <br />}{l}</React.Fragment>)}
    {postcode && <>{lines.length > 0 && <br />}<span className={MONO}>{postcode}</span></>}
  </div>
)

// ── edit helpers ─────────────────────────────────────────────
// A label with the dashed condition tag ("shown for Other").
export const CondLabel: React.FC<{ label: string; cond: string }> = ({ label, cond }) => (
  <span className="inline-flex items-center gap-1.5">{label} <CondTag>{cond}</CondTag></span>
)
// mockup .revealed — a conditional field, indented under a 3px rule.
export const REVEALED = 'border-l-[3px] border-casevo-mid/55 pl-3 ml-[2px]'
export const HINT = 'text-[12px] text-casevo-text-soft mt-1'

// Segmented control (mockup .seg) built from REAL radios, so the live-sync
// engine's delegated `change` listener fires exactly as for the old radios.
const SEG_WRAP = 'inline-flex border border-casevo-border rounded-[9px] overflow-hidden bg-casevo-surface h-[38px] max-w-full'
const segCls = (on: boolean): string =>
  `relative inline-flex items-center gap-1.5 px-[14px] text-[13.5px] border-r border-casevo-border last:border-r-0 cursor-pointer whitespace-nowrap has-[:focus-visible]:shadow-[inset_0_0_0_2px_var(--casevo-focus)] ${on ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/40'}`
export interface SegOption { label: React.ReactNode; checked: boolean; onSelect: () => void }
export const SegRadios: React.FC<{ name: string; ariaLabel: string; options: SegOption[] }> = ({ name, ariaLabel, options }) => (
  <div role="radiogroup" aria-label={ariaLabel} className={SEG_WRAP}>
    {options.map((o, i) => (
      <label key={i} className={segCls(o.checked)}>
        <input type="radio" className="sr-only" name={name} checked={o.checked} onChange={o.onSelect} />
        {o.label}
      </label>
    ))}
  </div>
)
// Same look, plain buttons (for a control whose old version was buttons with
// explicit broadcasts — the Accident location mode).
export const SegButtons: React.FC<{ ariaLabel: string; options: SegOption[] }> = ({ ariaLabel, options }) => (
  <div role="group" aria-label={ariaLabel} className={SEG_WRAP}>
    {options.map((o, i) => (
      <button key={i} type="button" aria-pressed={o.checked} onClick={o.onSelect} className={`${segCls(o.checked)} focus:outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--casevo-focus)]`}>
        {o.label}
      </button>
    ))}
  </div>
)

// Checkbox tile (mockup .itl) — a REAL checkbox inside a label.
export const Tile: React.FC<{ label: string; checked: boolean; onChange: (checked: boolean) => void }> = ({ label, checked, onChange }) => (
  <label className={`flex items-center gap-[9px] px-[11px] py-[9px] border rounded-[10px] text-[13.5px] cursor-pointer transition-colors has-[:focus-visible]:shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_25%,transparent)] ${checked ? 'border-casevo-brand bg-casevo-mint/60 font-semibold text-casevo-forest' : 'border-casevo-border bg-casevo-surface text-casevo-forest hover:border-casevo-mid'}`}>
    <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span aria-hidden="true" className={`w-[17px] h-[17px] rounded-[5px] border-[1.5px] grid place-items-center shrink-0 ${checked ? 'bg-casevo-primary border-casevo-primary text-white' : 'border-casevo-border'}`}>
      {checked && <KIcon name="check" size={11} strokeWidth={3.5} />}
    </span>
    {label}
  </label>
)

// ── live documents ───────────────────────────────────────────
// The rich-text fields are Yjs collab documents — always live, never part of
// a card's Cancel snapshot. The editor itself is the shared RichTextEditor
// (unchanged props); these wrappers only frame it to the mockup's .rt/.rtb/
// .rtc look via descendant overrides scoped to these instances.
const RT_WRAP =
  'relative [&>div:first-child]:!rounded-[10px] [&>div:first-child]:!bg-casevo-surface [&>div:first-child]:overflow-hidden ' +
  '[&>div:first-child>div:first-child]:bg-casevo-input [&>div:first-child>div:first-child]:!border-casevo-border-soft ' +
  '[&>div:first-child>div:first-child]:!px-[6px] [&>div:first-child>div:first-child]:!py-[5px] [&>div:first-child>div:first-child]:!pr-[150px] ' +
  '[&_.ProseMirror]:!px-[14px] [&_.ProseMirror]:!py-[12px] [&_.ProseMirror]:!min-h-[70px] [&_.ProseMirror]:!text-[14px] [&_.ProseMirror]:!leading-[1.6] ' +
  '[&_.ProseMirror_p]:mb-2 [&_.ProseMirror_p:last-child]:mb-0 [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_ol]:list-decimal'
export type CollabStatusLike = 'connecting' | 'connected' | 'disconnected' | string
// Render-prop so the caller keeps writing its own <RichTextEditor …/> with the
// exact same props; we only add the status observer for the "Live" badge.
export const LiveDocEdit: React.FC<{ children: (onStatus: (s: CollabStatusLike) => void) => React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<CollabStatusLike>('connecting')
  return (
    <div className={RT_WRAP}>
      {children(setStatus)}
      {status === 'connected' && (
        <span className="pointer-events-none absolute right-[10px] top-[9px] inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-casevo-text-soft">
          <i className="w-[7px] h-[7px] rounded-full bg-[#52a87f]" aria-hidden="true" />Live · co-edited
        </span>
      )}
    </div>
  )
}
export const LiveNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center gap-1.5 text-[12px] text-casevo-text-soft mt-[7px]"><KIcon name="sync" size={13} />{children}</div>
)

// Prose (mockup .prose, :327-328) for the read-only live view.
export const PROSE =
  'text-[14.5px] leading-[1.62] text-casevo-forest [&_p]:mb-[9px] [&_p:last-child]:mb-0 [&_ul]:pl-5 [&_ul]:list-disc [&_ol]:pl-5 [&_ol]:list-decimal [&_strong]:font-semibold [&_b]:font-semibold'
export const htmlHasText = (s: string | null | undefined): boolean =>
  !!s && s.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== ''
// Plain text of a stored HTML mirror (glance strips, word counts). Parsed
// inertly (DOMParser never runs scripts or loads resources).
export const htmlText = (s: string | null | undefined): string => {
  if (!s) return ''
  const doc = new DOMParser().parseFromString(s.replace(/<\/(p|li|h\d)>/gi, '</$1> '), 'text/html')
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim()
}
export const wordCount = (s: string | null | undefined): number => {
  const t = htmlText(s)
  return t ? t.split(' ').length : 0
}

// VIEW of a live document = its stored readable HTML mirror (the copy every
// editor's snapshot writes), sanitised. Instant and connection-free, like the
// task modal's fast path; opening the card for editing mounts the live Yjs
// editor (which also repairs a stale mirror).
export const MirrorProse: React.FC<{ html: string; className?: string }> = ({ html, className = PROSE }) => (
  // eslint-disable-next-line react/no-danger
  <div className={className} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
)

// LIVE VIEW of a live document (read-only Yjs): viewers see a colleague's
// typing + carets as it happens. One provider per visible doc (like the old
// always-mounted forms); its own `view:` key so view↔edit remount cleanly.
// ★ Fallback (RichTextEditor collabFallback): the stored copy (or `empty`)
// renders IMMEDIATELY and stays until the live doc has synced — and stays for
// good if the collab server is unreachable — so a view never shows a spinner.
// Same initialHtml/onSnapshot as the edit editor, so first-open seeding and
// the mirror repair behave exactly as before.
export const LiveDocView: React.FC<{
  docName: string
  html: string
  onSnapshot: (html: string) => void
  collabUser?: CollabUser
  empty: React.ReactNode
  // Optional typography override (e.g. the one-line deadline note summary).
  proseClassName?: string
}> = ({ docName, html, onSnapshot, collabUser, empty, proseClassName = PROSE }) => (
  <RichTextEditor
    key={`view:${docName}`}
    collabDocName={docName}
    collabToken={() => localStorage.getItem('casevo_token') ?? ''}
    initialHtml={DOMPurify.sanitize(html ?? '')}
    onSnapshot={onSnapshot}
    collabUser={collabUser}
    collabReadOnly
    collabReadOnlyClassName={`${proseClassName} [&_.ProseMirror]:outline-none`}
    collabFallback={htmlHasText(html) ? <MirrorProse html={html} className={proseClassName} /> : empty}
    collabEmptyNode={empty}
  />
)

// Clamp (mockup .clamp/.more, :329-332) — max-height + fade; the "more" link
// either expands in place or runs `onMore` (e.g. open the card for editing).
export const Clamp: React.FC<{ maxH?: number; moreLabel: string; onMore?: () => void; showMore?: boolean; children: React.ReactNode }> = ({ maxH = 118, moreLabel, onMore, showMore = true, children }) => {
  const inner = useRef<HTMLDivElement>(null)
  const [over, setOver] = useState(false)
  const [open, setOpen] = useState(false)
  useLayoutEffect(() => {
    const el = inner.current
    if (!el) return
    const check = (): void => setOver(el.scrollHeight > maxH + 2)
    check()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [maxH])
  const clamped = over && !open
  return (
    <div>
      <div
        className={`relative overflow-hidden ${clamped ? "after:content-[''] after:absolute after:inset-x-0 after:bottom-0 after:h-[42px] after:bg-[linear-gradient(transparent,var(--casevo-surface))]" : ''}`}
        style={clamped ? { maxHeight: maxH } : undefined}
      >
        <div ref={inner}>{children}</div>
      </div>
      {showMore && (over || onMore) && (
        <button
          type="button"
          onClick={onMore ?? (() => setOpen((v) => !v))}
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-casevo-brand dark:text-[#8fd6b0] mt-2 focus:outline-none casevo-focus rounded-[3px]"
        >
          {onMore ? moreLabel : open ? 'Show less' : moreLabel}<KIcon name="right" size={13} />
        </button>
      )}
    </div>
  )
}
